import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import { marketingPalette, marketingSymbols } from '../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';
import PerformanceOverview from '../components/dashboard/PerformanceOverview';
import RecentHistories from '../components/dashboard/RecentHistories';
import { getCurrentUser } from '../services/auth';
import { getHistories, getStatistics } from '../services/historyService';
import useEntitlements from '../hooks/useEntitlements';
import useBillingVisibility from '../hooks/useBillingVisibility';
import { shouldDisplayCreditWallet } from '../utils/billingVisibility';

const palette = marketingPalette;

const fallbackStatistics = {
  totalHistories: 0,
  averageScore: 0,
  categoryAverages: {
    historyQuestions: 0,
    flow: 0,
    examinerQuestions: 0,
    empathy: 0,
  },
  recentHistories: [],
};

function DashboardPage() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const firstName = user?.firstName || 'there';
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(fallbackStatistics);
  const [histories, setHistories] = useState([]);
  const [error, setError] = useState('');
  const { status: entitlementStatus, loading: entitlementLoading } = useEntitlements({ enabled: true });
  const { shouldShowBilling } = useBillingVisibility();

  useEffect(() => {
    let active = true;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');

        const [statsResponse, historiesResponse] = await Promise.all([
          getStatistics(),
          getHistories(1, 6, 'sortBy=completedAt-desc'),
        ]);

        if (!active) {
          return;
        }

        setStatistics({
          ...fallbackStatistics,
          ...statsResponse,
          categoryAverages: {
            ...fallbackStatistics.categoryAverages,
            ...(statsResponse?.categoryAverages || {}),
          },
          recentHistories: Array.isArray(statsResponse?.recentHistories) ? statsResponse.recentHistories : [],
        });
        setHistories(Array.isArray(historiesResponse?.histories) ? historiesResponse.histories : []);
      } catch (fetchError) {
        console.error('Error fetching dashboard data:', fetchError);
        if (active) {
          setError('Failed to load dashboard data. Please try again.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      active = false;
    };
  }, []);

  const recentScores = useMemo(() => (
    Array.isArray(statistics?.recentHistories)
      ? statistics.recentHistories
          .map((item) => Number(item?.overallPercentage) || 0)
          .filter((value) => Number.isFinite(value))
      : []
  ), [statistics?.recentHistories]);

  const recentAverage = useMemo(() => {
    if (!recentScores.length) {
      return 0;
    }

    return recentScores.reduce((sum, value) => sum + value, 0) / recentScores.length;
  }, [recentScores]);

  const heroSummary = useMemo(() => {
    if (!statistics.totalHistories) {
      return 'No completed cases yet. Start a consultation to begin building a performance baseline.';
    }

    if (recentScores.length === 1) {
      return `You have completed ${statistics.totalHistories} case${statistics.totalHistories === 1 ? '' : 's'} so far. Your first marked score is ${recentScores[0].toFixed(1)}%.`;
    }

    return `You have completed ${statistics.totalHistories} cases with an average score of ${(Number(statistics.averageScore) || 0).toFixed(1)}%. Your recent ${recentScores.length}-case average is ${recentAverage.toFixed(1)}%.`;
  }, [recentAverage, recentScores, statistics.averageScore, statistics.totalHistories]);

  const walletBalance = Number(entitlementStatus?.wallet?.balance ?? 0);
  const usesPlatformCredits = shouldDisplayCreditWallet({
    accountType: user?.accountType,
    entitlementLoading,
    requiresTokens: entitlementStatus?.requiresTokens,
    shouldShowBilling,
  });
  const showBillingAction = user?.accountType === 'individual' && shouldShowBilling;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box
          sx={{
            borderRadius: { xs: '28px', md: '34px' },
            border: `1px solid ${alpha(palette.ink, 0.08)}`,
            bgcolor: alpha('#ffffff', 0.94),
            boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading your dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: '20px',
            border: `1px solid ${alpha('#b42318', 0.12)}`,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 3,
          p: { xs: 3, sm: 3.5, md: 4.5 },
          borderRadius: { xs: '28px', md: '34px' },
          background:
            'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
          color: palette.white,
          boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
        }}
      >
        <Box
          component="img"
          src={marketingSymbols.silhouette}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            right: { xs: -20, md: 28 },
            top: { xs: -34, md: -56 },
            width: { xs: 180, md: 280 },
            opacity: 0.14,
            pointerEvents: 'none',
          }}
        />

        <Stack spacing={{ xs: 3, md: 3.5 }} sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={1.4}>
            <Chip
              icon={<DashboardRoundedIcon sx={{ color: 'inherit !important' }} />}
              label="Practice dashboard"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: alpha('#ffffff', 0.12),
                color: palette.white,
                borderRadius: '999px',
                fontWeight: 800,
                fontFamily: publicBodyFontFamily,
              }}
            />
            <Typography
              component="h1"
              sx={{
                maxWidth: 940,
                fontFamily: publicHeadingFontFamily,
                fontSize: { xs: '2.2rem', md: '3rem' },
                lineHeight: 1.02,
                letterSpacing: '-0.04em',
              }}
            >
              {`Welcome back, ${firstName}.`}
            </Typography>
            <Typography
              sx={{
                maxWidth: 860,
                color: alpha('#ffffff', 0.84),
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.72,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {heroSummary}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1.4, md: 1.6 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ maxWidth: { xs: '100%', md: 1120 } }}
          >
            {usesPlatformCredits ? (
              <Box
                sx={{
                  p: 2,
                  minWidth: { md: 260 },
                  borderRadius: '22px',
                  bgcolor: alpha('#ffffff', 0.12),
                  border: `1px solid ${alpha('#ffffff', 0.14)}`,
                }}
              >
                <Stack direction="row" spacing={1.4} alignItems="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 42,
                      height: 42,
                      flex: '0 0 auto',
                      borderRadius: '16px',
                      bgcolor: alpha('#ffffff', 0.14),
                    }}
                  >
                    <AccountBalanceWalletRoundedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: alpha('#ffffff', 0.72), fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                      Credits left
                    </Typography>
                    <Typography sx={{ color: palette.white, fontWeight: 900, fontSize: '1.1rem', fontFamily: publicBodyFontFamily }}>
                      {walletBalance.toLocaleString()} credits left
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : null}
            {showBillingAction ? (
              <Button
                onClick={() => navigate('/account/billing')}
                endIcon={<ArrowOutwardRoundedIcon />}
                sx={{
                  ...publicSecondaryButtonSx,
                  minHeight: 52,
                  flex: { md: '0 1 210px' },
                  color: palette.white,
                  borderColor: alpha('#ffffff', 0.22),
                  bgcolor: alpha('#ffffff', 0.08),
                  '&:hover': {
                    borderColor: alpha('#ffffff', 0.32),
                    bgcolor: alpha('#ffffff', 0.14),
                  },
                }}
              >
                Buy credits
              </Button>
            ) : null}
            <Button
              onClick={() => navigate('/histories')}
              endIcon={<ArrowOutwardRoundedIcon />}
              sx={{
                ...publicPrimaryButtonSx,
                minHeight: 52,
                flex: { md: '0 1 210px' },
                bgcolor: palette.white,
                color: palette.heroBlueDeep,
                '&:hover': {
                  bgcolor: alpha('#ffffff', 0.92),
                },
              }}
            >
              Review cases
            </Button>
            <Button
              onClick={() => navigate('/community-library')}
              startIcon={<LibraryBooksRoundedIcon />}
              sx={{
                ...publicSecondaryButtonSx,
                minHeight: 52,
                flex: { md: '0 1 278px' },
                color: palette.white,
                borderColor: alpha('#ffffff', 0.22),
                bgcolor: alpha('#ffffff', 0.08),
                '&:hover': {
                  borderColor: alpha('#ffffff', 0.32),
                  bgcolor: alpha('#ffffff', 0.14),
                },
              }}
            >
              Explore community library
            </Button>
          </Stack>
        </Stack>
      </Box>

      {Array.isArray(entitlementStatus?.messages) && entitlementStatus.messages.length > 0 ? (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {entitlementStatus.messages
            .filter((message) => usesPlatformCredits || !String(message?.id || '').startsWith('wallet-'))
            .slice(0, 2)
            .map((message) => (
            <Alert
              key={message.id}
              severity={message.severity || 'info'}
              sx={{
                borderRadius: '20px',
                border: `1px solid ${alpha(palette.heroBlueMid, 0.12)}`,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography sx={{ fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                {message.title}
              </Typography>
              <Typography sx={{ mt: 0.25, fontFamily: publicBodyFontFamily }}>
                {message.body}
              </Typography>
            </Alert>
            ))}
        </Stack>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} xl={8}>
          <PerformanceOverview statistics={statistics} />
        </Grid>
        <Grid item xs={12} xl={4}>
          <RecentHistories histories={histories} loading={loading} onViewAll={() => navigate('/histories')} />
        </Grid>
      </Grid>
    </Container>
  );
}

export default DashboardPage;
